import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Category.css";
import "../styles/global.css";

/* ────────── Scroll Lock Fix Helper ────────── */
const unlockBodyScroll = () => {
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
  document.documentElement.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("padding-right");
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
};

/* ────────── Custom Pagination ────────── */
const ChevronLeftIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  const start = Math.min((currentPage - 1) * perPage + 1, totalRows);
  const end   = Math.min(currentPage * perPage, totalRows);

  return (
    <div className="cat-pagination">
      <div className="cat-pagination__info">
        <span>
          Showing <b>{start}</b>–<b>{end}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="cat-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="cat-pagination__pages">
        <button
          className="cat-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="cat-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`cat-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="cat-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

/* ────────── iPhone Toggle Switch ────────── */
const IosSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`ios-switch ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
  >
    <span className="ios-switch__thumb" />
  </button>
);

/* ────────── Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Main Component ────────── */
const Category = () => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filterText, setFilterText] = useState("");

  const [name, setName] = useState("");
  const [status, setStatus] = useState("Active");
  const [message, setMessage] = useState("");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editSlug, setEditSlug] = useState(null);

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const token = localStorage.getItem("authToken");

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleClearRows, setToggleClearRows] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [switchingSlug, setSwitchingSlug] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", confirmLabel: "Confirm",
    confirmVariant: "danger", onConfirm: null,
  });
  const showConfirm = ({ title, message, confirmLabel, confirmVariant = "danger", onConfirm }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm });
  };
  const closeConfirm = () => setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null }));

  /* ── Attach hidden.bs.modal listener ONCE to always unlock scroll ── */
  useEffect(() => {
    const modalEl = document.getElementById("addCategoryModal");
    if (!modalEl) return;

    const handleModalHidden = () => {
      unlockBodyScroll();
    };

    modalEl.addEventListener("hidden.bs.modal", handleModalHidden);
    return () => {
      modalEl.removeEventListener("hidden.bs.modal", handleModalHidden);
    };
  }, []);

  /* ── Image preview ── */
  useEffect(() => {
    if (!image) { setPreview(null); return; }
    const objectUrl = URL.createObjectURL(image);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  /* ── Fetch Categories ── */
  const fetchCategories = (page = 1, size = perPage) => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/categories?page=${page}&per_page=${size}`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setCategories(data.data);
          setFilteredCategories(data.data);
          setTotalRows(data.pagination.total);
          setCurrentPage(data.pagination.current_page);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCategories(1, perPage); }, []);

  /* ── Pagination handlers ── */
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCategories(page, perPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchCategories(1, newPerPage);
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  /* ── Client-side search filter ── */
  useEffect(() => {
    const result = categories.filter((cat) =>
      cat.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      cat.slug?.toLowerCase().includes(filterText.toLowerCase()) ||
      cat.status?.toLowerCase().includes(filterText.toLowerCase())
    );
    setFilteredCategories(result);
  }, [filterText, categories]);

  /* ── Status toggle ── */
  const handleStatusChange = (slug, currentStatus) => {
    if (switchingSlug === slug) return;
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Change Status",
      message: `Are you sure you want to set this category to "${newStatus}"?`,
      confirmLabel: "Yes, Change",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setSwitchingSlug(slug);
        fetch(`${API_BASE_URL}api/categories/status/${slug}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              setMessageAlert(`Status updated to ${newStatus} successfully!`);
              setMessageType("success");
              fetchCategories(currentPage, perPage);
            } else {
              setMessageAlert("Failed to update status.");
              setMessageType("danger");
            }
          })
          .catch(() => {
            setMessageAlert("Error while updating status.");
            setMessageType("danger");
          })
          .finally(() => {
            setSwitchingSlug(null);
            setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
          });
      },
    });
  };

  /* ── Close modal helper — uses hidden.bs.modal event to unlock scroll ── */
  const closeModal = useCallback(() => {
    const modalEl = document.getElementById("addCategoryModal");
    if (!modalEl) return;
    const modalInstance = window.bootstrap?.Modal?.getInstance(modalEl);
    if (modalInstance) {
      modalInstance.hide();
      // hidden.bs.modal event listener (registered in useEffect) will call unlockBodyScroll
    } else {
      // Modal instance not found — force cleanup immediately
      unlockBodyScroll();
    }
  }, []);

  /* ── Save category ── */
  const handleSaveCategory = (e) => {
    e.preventDefault();
    setMessage("");

    const formData = new FormData();
    formData.append("name", name);
    formData.append("is_home", 1);
    formData.append("status", status);
    if (image) formData.append("image", image);

    const url = isEditMode
      ? `${API_BASE_URL}api/categories/${editSlug}`
      : `${API_BASE_URL}api/categories`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          setMessageAlert(isEditMode ? "Category updated successfully!" : "Category added successfully!");
          setMessageType("success");
          setName("");
          setStatus("Active");
          setImage(null);
          setPreview(null);
          setIsEditMode(false);
          fetchCategories(currentPage, perPage);
          // Close modal — scroll unlock is handled by hidden.bs.modal listener
          closeModal();
        } else {
          setMessageAlert("Failed to save category.");
          setMessageType("danger");
        }
      })
      .catch(() => { setMessageAlert("Something went wrong!"); setMessageType("danger"); })
      .finally(() => { setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000); });
  };

  /* ── Edit handler ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditSlug(row.slug);
    setName(row.name);
    setStatus(row.status);
    setPreview(row.image ? `${API_BASE_URL}public/category/${row.image}` : `${API_BASE_URL}public/noimage.jpg`);
    setImage(null);
    const modalEl = document.getElementById("addCategoryModal");
    new window.bootstrap.Modal(modalEl).show();
  };

  /* ── Single Delete ── */
  const handleDelete = (slug) => {
    showConfirm({
      title: "Delete Category",
      message: "Are you sure you want to delete this category? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/categories/destroy/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              setMessageAlert("Category deleted successfully!");
              setMessageType("success");
              fetchCategories(currentPage, perPage);
            } else {
              setMessageAlert("Failed to delete category.");
              setMessageType("danger");
            }
          })
          .catch(() => { setMessageAlert("Error deleting category."); setMessageType("danger"); })
          .finally(() => { setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000); });
      },
    });
  };

  /* ── Bulk Delete ── */
  const handleSelectedRowsChange = ({ selectedRows: rows }) => setSelectedRows(rows);

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected categor${count === 1 ? "y" : "ies"}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count === 1 ? "" : "s"}`,
      confirmVariant: "danger",
      onConfirm: async () => {
        closeConfirm();
        setBulkDeleting(true);
        try {
          const results = await Promise.all(
            selectedRows.map((row) =>
              fetch(`${API_BASE_URL}api/categories/destroy/${row.slug}`, {
                method: "GET",
                headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
              })
                .then((res) => res.json())
                .catch(() => ({ status: false }))
            )
          );
          const successCount = results.filter((r) => r.status).length;
          const failCount = count - successCount;
          if (failCount === 0) {
            setMessageAlert(`${successCount} categor${successCount === 1 ? "y" : "ies"} deleted successfully!`);
            setMessageType("success");
          } else {
            setMessageAlert(`${successCount} deleted, ${failCount} failed.`);
            setMessageType(successCount > 0 ? "warning" : "danger");
          }
          setSelectedRows([]);
          setToggleClearRows((p) => !p);
          fetchCategories(currentPage, perPage);
        } catch {
          setMessageAlert("Error during bulk delete.");
          setMessageType("danger");
        } finally {
          setBulkDeleting(false);
          setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
        }
      },
    });
  };

  /* ── DataTable custom styles ── */
  const customStyles = useMemo(() => ({
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
        fontFamily: "'Inter', sans-serif",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  }), []);

  /* ── Table Columns ── */
  const columns = useMemo(() => [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
      cell: (row) => (
        <span className="cat-id-pill">#{row.id}</span>
      ),
    },
    {
      name: "Image",
      width: "90px",
      cell: (row) => (
        <div className="cat-img-wrap">
          <img
            src={row.image ? `${API_BASE_URL}public/category/${row.image}` : `${API_BASE_URL}public/noimage.jpg`}
            alt={row.name}
          />
        </div>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => <span className="cat-name-cell">{row.name}</span>,
    },
    {
      name: "Slug",
      selector: (row) => row.slug,
      cell: (row) => <span className="cat-slug-cell">{row.slug}</span>,
    },
    {
      name: "Status",
      cell: (row) => (
        <div className="cat-status-wrap">
          <IosSwitch
            checked={row.status === "Active"}
            onChange={() => handleStatusChange(row.slug, row.status)}
            disabled={switchingSlug === row.slug}
          />
          <span className={`cat-status-label ${row.status === "Active" ? "is-active" : "is-inactive"}`}>
            {row.status === "Active" ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      width: "140px",
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => (
        <span className="cat-date-cell">
          {new Date(row.created_at).toLocaleDateString("en-IN")}
        </span>
      ),
      width: "130px",
    },
    {
      name: "Actions",
      width: "110px",
      ignoreRowClick: true,
      cell: (row) => (
        <div className="cat-actions">
          <button className="cat-action-btn cat-edit" onClick={() => handleEdit(row)} title="Edit Category" type="button">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button className="cat-action-btn cat-delete" onClick={() => handleDelete(row.slug)} title="Delete Category" type="button">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      ),
    },
  ], [switchingSlug]);

  return (
    <>
      <div id="layout-wrapper" className="cat-page">
        <Header />
        {(loading || bulkDeleting) && (
          <div className="cat-overlay"><div className="cat-spinner" /></div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">

              {/* Page Header */}
              <div className="cat-header">
                <div className="cat-header-left">
                  <h1>Categories</h1>
                  <p>Manage your product categories — add, edit, or organize them.</p>
                </div>
                <button
                  type="button"
                  className="cat-add-btn"
                  data-bs-toggle="modal"
                  data-bs-target="#addCategoryModal"
                  onClick={() => {
                    setIsEditMode(false); setName(""); setStatus("Active");
                    setImage(null); setPreview(null);
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Category
                </button>
              </div>

              {/* Card */}
              <div className="cat-card">

                {/* Toolbar */}
                <div className="cat-card-toolbar">
                  <div className="cat-search">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by name, slug or status…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <div className="cat-stats">
                    <span className="cat-stat-chip">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1.5" />
                        <rect x="14" y="3" width="7" height="7" rx="1.5" />
                        <rect x="3" y="14" width="7" height="7" rx="1.5" />
                        <rect x="14" y="14" width="7" height="7" rx="1.5" />
                      </svg>
                      Total: <b>{totalRows}</b>
                    </span>
                  </div>
                </div>

                {/* Alert */}
                {messagealert && (
                  <div className={`cat-alert ${messageType}`} role="alert">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {messagealert}
                    <button type="button" className="close" onClick={() => setMessageAlert("")}>×</button>
                  </div>
                )}

                {/* Bulk action bar */}
                {selectedRows.length > 0 && (
                  <div className="cat-bulk-bar">
                    <div className="cat-bulk-info">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                        <polyline points="9 11 12 14 22 4" />
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                      </svg>
                      <strong>{selectedRows.length}</strong>
                      &nbsp;item{selectedRows.length === 1 ? "" : "s"} selected
                    </div>
                    <div className="cat-bulk-actions">
                      <button
                        type="button"
                        className="cat-bulk-clear"
                        onClick={() => { setSelectedRows([]); setToggleClearRows(!toggleClearRows); }}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="cat-bulk-delete"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                        {bulkDeleting ? "Deleting…" : "Delete Selected"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Table */}
                <DataTable
                  columns={columns}
                  data={filteredCategories}
                  customStyles={customStyles}
                  highlightOnHover
                  striped
                  responsive
                  selectableRows
                  onSelectedRowsChange={handleSelectedRowsChange}
                  clearSelectedRows={toggleClearRows}
                  pagination={false}
                  noDataComponent={
                    <div className="cat-empty">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                        <line x1="12" y1="22.08" x2="12" y2="12" />
                      </svg>
                      <h4>No categories yet</h4>
                      <p>Click "Add Category" to create your first one.</p>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={handlePerPageChange}
                  />
                )}

              </div>
            </div>
          </div>

          {/* Add / Edit Modal */}
          <div
            className="modal fade"
            id="addCategoryModal"
            tabIndex="-1"
            aria-labelledby="addCategoryModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <form id="addEventForm" onSubmit={handleSaveCategory}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="addCategoryModalLabel">
                      {isEditMode ? "Edit Category" : "Add New Category"}
                    </h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                  </div>
                  <div className="modal-body">
                    <div className="row g-4">
                      <div className="col-12">
                        <label htmlFor="catName" className="form-label">Name</label>
                        <input
                          type="text" className="form-control" id="catName"
                          placeholder="Category name" value={name}
                          onChange={(e) => setName(e.target.value)} required
                        />
                      </div>
                      <div className="col-12">
                        <label htmlFor="catImage" className="form-label">Image</label>
                        <input
                          type="file" className="form-control" id="catImage"
                          accept="image/*" onChange={(e) => setImage(e.target.files[0])}
                        />
                        {preview && (
                          <img
                            src={preview} alt="Preview"
                            style={{ width: 84, height: 84, marginTop: 12, borderRadius: 10, objectFit: "cover", border: "1.5px solid #e3f0e9" }}
                          />
                        )}
                      </div>
                      <div className="col-6">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select" id="statusData" name="status"
                          value={status} onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value="Active">Active</option>
                          <option value="InActive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                    <button type="submit" className="btn btn-primary">
                      {isEditMode ? "Update Category" : "Add Category"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Custom Confirm Dialog */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Category;